import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { UserService } from '../../services/api';
import type { User } from '../../types';
import HelpLink from '../HelpLink';

/**
 * Auto-dismiss delay for the profile-update success Alert (issue #1947).
 *
 * Matches the existing `BillingPanel` digest-frequency success Alert so the
 * Settings page presents a single, consistent feedback rhythm.
 */
const SUCCESS_AUTODISMISS_MS = 3000;

/**
 * Settings → Profile panel (issues #1947, #2169).
 *
 * Renders the first / last / email fields and the "Save Profile" submit.
 * Email is read-only because email-change requires Cognito verification —
 * deferred per the original issue's "optional" framing.
 *
 * The password-change form previously lived here too; #2169 moved it to
 * the Security tab where it sits with the rest of the security-sensitive
 * actions (sign-out-everywhere, 2FA enrollment).
 *
 * Save behavior:
 * - Success Alert auto-dismisses after {@link SUCCESS_AUTODISMISS_MS}.
 * - Error Alert stays until the next submit so the user can read it.
 * - Save button disables in-flight to prevent double submits.
 */
export default function ProfilePanel() {
    const [user, setUser] = useState<User | null>(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [loadError, setLoadError] = useState<string | null>(null);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Fetch /api/me on mount. The `cancelled` guard avoids the
    // setState-on-unmounted-component warning if the user navigates away
    // before the promise resolves.
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const me = await UserService.getMe();
                if (!cancelled) {
                    setUser(me);
                    setFirstName(me.firstName);
                    setLastName(me.lastName);
                }
            } catch {
                if (!cancelled) {
                    setLoadError('Failed to load profile. Please try again.');
                }
            }
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleProfileSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSaveError(null);
        setSaveSuccess(false);
        setSaving(true);
        try {
            const updated = await UserService.updateMe({ firstName, lastName });
            setUser(updated);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), SUCCESS_AUTODISMISS_MS);
        } catch {
            setSaveError('Failed to update profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                    Profile
                </Typography>
                <Alert severity="error" role="alert">
                    {loadError}
                </Alert>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="h6" component="h2">
                    Profile
                </Typography>
                <HelpLink slug="account-settings" />
            </Box>
            {/* #2559 — the email caveat used to live here too, but the same
                constraint is communicated by the helper directly under the
                Email field below (~80px away). Kept the under-field helper
                because it sits at the affordance the user is trying to use. */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Update your display name.
            </Typography>

            <Box component="form" onSubmit={handleProfileSubmit}>
                <Stack spacing={2}>
                    <TextField
                        label="First name"
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        disabled={saving || !user}
                        slotProps={{ htmlInput: { 'aria-label': 'First name' } }}
                        fullWidth
                    />
                    <TextField
                        label="Last name"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        disabled={saving || !user}
                        slotProps={{ htmlInput: { 'aria-label': 'Last name' } }}
                        fullWidth
                    />
                    {/* #2180 — Email was rendered as `<TextField disabled>`,
                        which paints the value in muted gray and reads as
                        empty placeholder text. Treat it as a read-only
                        value-display block instead: a Typography label
                        above a normal-color value, with the helper-text
                        caption underneath. Communicates "this is your
                        email, you can't edit it here" without the
                        looks-empty bug. */}
                    <Box>
                        <Typography variant="body2" component="label" id="profile-email-label" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Email
                        </Typography>
                        <Typography
                            variant="body1"
                            component="div"
                            aria-labelledby="profile-email-label"
                            data-testid="profile-email-value"
                            sx={{ fontWeight: 500 }}
                        >
                            {user?.email ?? '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                            Email cannot be changed here. Contact an administrator if you need to update it.
                        </Typography>
                    </Box>

                    {saveSuccess && (
                        <Alert severity="success" role="status">
                            Profile updated.
                        </Alert>
                    )}
                    {saveError && (
                        <Alert severity="error" role="alert">
                            {saveError}
                        </Alert>
                    )}

                    <Box>
                        <Button type="submit" variant="contained" disabled={saving || !user}>
                            {saving ? 'Saving...' : 'Save Profile'}
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Paper>
    );
}
